import type { SkillData } from '@sb/feathers-types';
import type { EquipmentItem } from '@sb/integrations/types';
import type { Step as RcStep } from '@sb/remote-control/types';
import { getStepKindInfo } from '@sb/remote-control/util/stepKindInfo';
import type { Space, StepKind } from '@sb/routine-runner';
import { DEFAULT_SPEED_PROFILE } from '@sb/routine-runner/speed-profile/SpeedProfile';

import {
  type Diagnostic,
  type RoutineView,
  type RuleEvaluationContext,
  type ValidationRule,
} from '../../types';
import { ActionRequiredError, type WarningFinding } from '../../util/errors';
import {
  createStepRule,
  errorToDiagnostics,
  readActionRequired,
  type Warn,
} from '../../util/ruleHelpers';
import { getRunnerArgsSchema } from '../../util/runnerSchemas';

function ensureArgsWithArgumentKind(
  stepKind: StepKind,
  args: unknown,
): RcStep.Arguments {
  const isObj =
    args != null && typeof args === 'object' && !Array.isArray(args);

  const hasArgumentKind =
    isObj && (args as Record<string, unknown>)?.argumentKind != null;

  if (isObj && hasArgumentKind) return args as RcStep.Arguments;
  if (isObj)
    return { argumentKind: stepKind, ...(args as object) } as RcStep.Arguments;

  return { argumentKind: stepKind } as RcStep.Arguments;
}

function findStepNode(
  routine: RoutineView,
  stepId: string,
): { id: string; stepKind: StepKind | string; children: string[] } | null {
  const s = routine.steps.find((x) => x.id === stepId);

  return s
    ? { id: s.id, stepKind: s.stepKind, children: s.children ?? [] }
    : null;
}

/**
 * The step kind's operator-facing name, as the step library and the editor's
 * step rows show it (e.g. `WaitForConfirmation` reads "Prompt User"). Falls
 * back to the raw kind for a kind with no registered info.
 */
export function stepKindDisplayName(stepKind: StepKind | string): string {
  try {
    return getStepKindInfo(stepKind as StepKind).name ?? String(stepKind);
  } catch {
    return String(stepKind);
  }
}

export function createCanonicalStepRule(opts: {
  id: string;
  stepKind: StepKind | string;
  validate: (args: CanonicalValidateArgs) => void;
}): (args: {
  routine: RoutineView;
  ctx: RuleEvaluationContext;
  stepId: string;
  warn: Warn;
}) => Diagnostic[] {
  return ({ routine, ctx, stepId, warn }) => {
    const node = findStepNode(routine, stepId);
    if (!node) return [];

    type RoutineViewWithDerived = RoutineView & {
      derived?: {
        parentsById?: Record<
          string,
          Array<{ id: string; stepKind: StepKind | string }>
        >;
      };
    };

    const parents =
      (routine as RoutineViewWithDerived).derived?.parentsById?.[stepId] ?? [];

    const children =
      (node.children ?? [])
        .map((cid) => findStepNode(routine, cid))
        .filter((x): x is NonNullable<typeof x> => x != null)
        .map((c) => ({ id: c.id, stepKind: c.stepKind })) ?? [];

    const stepCfg = routine.stepConfigurations[stepId];

    const ensuredArgs = ensureArgsWithArgumentKind(
      node.stepKind as StepKind,
      stepCfg?.args as unknown,
    );

    try {
      opts.validate({
        stepId,
        stepKind: node.stepKind,
        parents,
        children,
        args: ensuredArgs,
        routine,
        ctx,
        warn,
      });
    } catch (err) {
      return errorToDiagnostics(err, stepId, opts.id);
    }

    return [];
  };
}

export function buildStepRule({
  id,
  stepKind,
  runner,
}: {
  id: string;
  stepKind: string;
  runner: (args: {
    routine: RoutineView;
    ctx: RuleEvaluationContext;
    stepId: string;
    warn: Warn;
  }) => Diagnostic[];
}) {
  return createStepRule({
    id,
    stepKinds: [stepKind],
    run: runner,
  });
}

export type CanonicalValidateArgs = {
  stepId: string;
  stepKind: StepKind | string;
  parents: Array<{ id: string; stepKind: StepKind | string }>;
  children: Array<{ id: string; stepKind: StepKind | string }>;
  args: RcStep.Arguments;
  routine: RoutineView;
  ctx: RuleEvaluationContext;
  /**
   * Hand a non-blocking finding to the rule's collector rather than throwing
   * it. A throw ends the step's remaining checks, so a warning that threw
   * could stand in for a blocking finding one of them would have raised.
   * Ignores `undefined`, so a validator's return value passes straight in.
   */
  warn: Warn;
};

export abstract class CanonicalStepRuleBase {
  public readonly id: string;

  public readonly stepKind: string;

  protected constructor(id: string, stepKind: string) {
    this.id = id;
    this.stepKind = stepKind;
  }

  /**
   * Ensure the provided args match this.stepKind.
   * Throws TypeError if the argument kind does not match.
   *
   * @throws {TypeError} When args.argumentKind !== this.stepKind
   */
  protected ensureArgumentKind(
    args: { argumentKind?: string } | unknown,
  ): void {
    const argKind = (args as { argumentKind?: string } | undefined)
      ?.argumentKind;

    if (argKind !== this.stepKind) {
      throw new TypeError(`Expected argument kind ${this.stepKind}`);
    }
  }

  /**
   * Combine global and routine space items into a single array.
   *
   * @returns The combined space array.
   */
  protected getSpaceItems(
    routine: { space?: Space.Item[] } | undefined,
    ctx: { globalSpace?: Space.Item[] } | undefined,
  ): Space.Item[] {
    return [
      ...((ctx?.globalSpace ?? []) as Space.Item[]),
      ...((routine?.space ?? []) as Space.Item[]),
    ];
  }

  /**
   * Find equipment by id in the validation context.
   *
   * @returns {any|undefined} The equipment config if found
   */
  protected findEquipment(
    ctx: { equipment?: EquipmentItem[] } | undefined,
    id: string,
  ): EquipmentItem | undefined {
    return (ctx?.equipment ?? []).find((e) => e.id === id);
  }

  /** Find a persisted skill by id in the validation context. */
  protected findSkill(
    ctx: { skills?: SkillData[] } | undefined,
    id: string,
  ): SkillData | undefined {
    return (ctx?.skills ?? []).find((s) => s.id === id);
  }

  /**
   * The step's skill, from the robot's skills when the caller supplied them.
   * An id missing from that list, or naming another kind of skill, is a step
   * with no usable skill, and reads the same as one with none chosen.
   * Undefined when the caller supplied no list: nothing is known about it.
   *
   * @throws {ActionRequiredError} with `noSkillMessage` on `skillId`.
   */
  protected resolveSkill(
    ctx: { skills?: SkillData[] } | undefined,
    id: string,
    skillType: SkillData['skillType'],
    noSkillMessage: string,
  ): SkillData | undefined {
    if (!ctx?.skills) return undefined;

    const skill = this.findSkill(ctx, id);

    if (skill?.skillType !== skillType) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: noSkillMessage,
        fieldId: 'skillId',
      });
    }

    return skill;
  }

  /**
   * A draft is never run (botman and the ShowPath runner refuse one), but the
   * agent attaches its own drafts to steps, so the step is kept and warned
   * about rather than read as having no skill.
   */
  protected draftSkillWarning(skill: SkillData): WarningFinding | undefined {
    if (!skill.isDraft) return undefined;

    return {
      kind: 'draftSkill',
      message: `'${skill.name}' is a draft. The routine can't run this step until the skill is finished and saved.`,
      fieldId: 'skillId',
    };
  }

  /**
   * The step's args in routine-runner shape, or `undefined` when the kind has
   * no converter or the conversion failed for a reason it cannot describe.
   *
   * @throws {ActionRequiredError} when the converter names the problem itself.
   *   That finding is written in operator terms and belongs to the caller; a
   *   schema complaint about UI-shaped args cannot say anything as useful.
   */
  private toRunnerArgs(
    args: CanonicalValidateArgs,
    originalArgs: Record<string, unknown>,
  ): Record<string, unknown> | undefined {
    const toRR = getStepKindInfo(this.stepKind as StepKind).toRoutineRunner;

    if (typeof toRR !== 'function') return undefined;

    let converted;

    try {
      converted = toRR({
        // `toRoutineRunner` only reads stepKind / args; pass minimal stubs
        // for the other fields it requires by type.
        stepConfiguration: {
          id: args.stepId,
          stepKind: this.stepKind as StepKind,
          args: originalArgs as unknown,
        } as any,
        stepData: { id: args.stepId } as any,
        equipment: args.ctx?.equipment ?? [],
        baseSpeedProfile: DEFAULT_SPEED_PROFILE,
        codeBlocks: [],
      });
    } catch (err) {
      if (readActionRequired(err)) throw err;

      // A converter that broke for its own reasons must not mask a genuine
      // config error, so the caller falls back to the UI args.
      return undefined;
    }

    const convertedArgs = (converted as { args?: unknown })?.args;

    return convertedArgs && typeof convertedArgs === 'object'
      ? (convertedArgs as Record<string, unknown>)
      : undefined;
  }

  // Cross-check that the step's storage/UI args produce something that
  // survives the routine runner's Zod schema. The conversion is what makes
  // this safe to run on UI shape — fields are renamed in runner shape (e.g.
  // SetIO outputs → changes) — and falls back to the UI args when it cannot
  // convert.
  protected runnerSchemaValidate(args: CanonicalValidateArgs): void {
    const schema = getRunnerArgsSchema(this.stepKind);
    if (!schema) return;

    const originalArgs = (args.args ?? {}) as Record<string, unknown>;
    const { argumentKind: _argumentKind, ...rest } = originalArgs;

    const candidate = this.toRunnerArgs(args, originalArgs) ?? rest;

    // Validate only the keys present (treat missing keys as ok); prefer deep partial when available.
    const schemaAny = schema as unknown as {
      deepPartial?: () => typeof schema;
      partial?: () => typeof schema;
    };

    const partialSchema =
      schemaAny.deepPartial?.() ?? schemaAny.partial?.() ?? schema;

    const result = partialSchema.safeParse(candidate);
    if (result.success) return;

    const issue = result.error.issues[0];

    const fieldId =
      Array.isArray(issue?.path) && issue.path.length > 0
        ? String(issue.path[0])
        : undefined;

    throw new ActionRequiredError({
      kind: 'invalidConfiguration',
      message: issue?.message || 'Arguments are not valid for this step',
      fieldId,
    });
  }

  protected abstract validate(args: CanonicalValidateArgs): void;

  public toValidationRule(): ValidationRule {
    const runner = createCanonicalStepRule({
      id: this.id,
      stepKind: this.stepKind,
      validate: (args) => {
        // Validations occur in priority order:
        // 1) Ensure arguments are for the expected step kind
        this.ensureArgumentKind(args.args);
        // 2) Run subclass-provided validations
        this.validate(args);
        // 3) Validate against the runner schema
        this.runnerSchemaValidate(args);
      },
    });

    return buildStepRule({
      id: this.id,
      stepKind: this.stepKind,
      runner,
    });
  }
}
